import React from 'react';
import { motion } from 'framer-motion';
import { assets } from '../lib/assets';

// Fruit-bearing trees available in the Pallavit orchard.
// Broken image URLs gracefully fall back to the orchard fruit-basket asset.
export const TREES = [
  { key: 'mango', name: 'Mango', local: 'Aam', copy: 'The king of fruits — summer in every bite.',
    img: 'https://images.unsplash.com/photo-1744087180144-bab664b51900?auto=format&fit=crop&w=900&q=80' },
  { key: 'guava', name: 'Guava', local: 'Amrood', copy: 'Generous, fragrant, unfussy — a tree that feels like home.',
    img: 'https://images.unsplash.com/photo-1759772515773-5748e3de1beb?auto=format&fit=crop&w=900&q=80' },
  { key: 'lemon', name: 'Lemon', local: 'Nimbu', copy: 'Year-round sunshine. Small tree, endless gifts.',
    img: 'https://images.unsplash.com/photo-1687489577775-2b054a0dab3d?auto=format&fit=crop&w=900&q=80' },
  { key: 'orange', name: 'Orange', local: 'Santra', copy: 'Nagpur gold — bright, juicy, unmistakable.',
    img: 'https://images.unsplash.com/photo-1547514701-42782101795e?auto=format&fit=crop&w=900&q=80' },
  { key: 'jamun', name: 'Jamun', local: 'Java Plum', copy: 'Monsoon purple — stains your fingers and memory.',
    img: '/images/jamun.jpg' },
  { key: 'chiku', name: 'Chiku', local: 'Sapota', copy: 'Caramel-sweet flesh that ripens with patience.',
    img: 'https://images.unsplash.com/photo-1699863164964-13805b27bc42?auto=format&fit=crop&w=900&q=80' },
  { key: 'sitaphal', name: 'Sitaphal', local: 'Custard Apple', copy: 'Quiet, knobbly, impossibly sweet custard inside.',
    img: 'https://images.unsplash.com/photo-1676993842546-ff9b61c68abc?auto=format&fit=crop&w=900&q=80' },
  { key: 'pomegranate', name: 'Pomegranate', local: 'Anar', copy: 'Ruby seeds, ancient symbol of fertility and good fortune.',
    img: 'https://images.unsplash.com/photo-1541344999736-83eca272f6fc?auto=format&fit=crop&w=900&q=80' },
];

function TreeCard({ t, onClick, index }) {
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06 }}
      whileHover={{ y: -4 }}
      className="group text-left bg-brand-bg rounded-2xl overflow-hidden border border-brand-line hover:border-brand-gold/60 transition-all duration-500"
      data-testid={`tree-card-${t.key}`}
    >
      <div className="aspect-square overflow-hidden bg-brand-bg2">
        <img
          src={t.img}
          alt={t.name}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== assets.fruitBasket) e.currentTarget.src = assets.fruitBasket;
          }}
          className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
        />
      </div>
      <div className="p-3">
        <div className="font-serif text-base md:text-lg text-brand-ink leading-tight font-medium">{t.name}</div>
        <div className="text-brand-gold italic font-serif text-[11px] mt-0.5 line-clamp-1">{t.local}</div>
      </div>
    </motion.button>
  );
}

export default function TreeOptions({ onGift, onSelect }) {
  const handlePick = (key) => {
    onSelect && onSelect(key);
    onGift && onGift();
  };

  return (
    <section id="trees" className="bg-brand-bg2 py-28 md:py-36" data-testid="trees-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-5 font-medium">The Trees</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight font-light">
              A grove of <em className="text-brand-forest">living possibilities.</em>
            </h2>
          </div>
          <p className="md:max-w-xs text-brand-ink2 text-sm italic font-serif">
            Looking for something specific? <a href="https://wa.me/919403707272" className="underline decoration-brand-gold underline-offset-4 hover:text-brand-forest">Ask us</a> — we often plant by special request.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {TREES.map((t, i) => (
            <TreeCard key={t.key} t={t} index={i} onClick={() => handlePick(t.key)} />
          ))}
        </div>
      </div>
    </section>
  );
}
